import cx from "classnames";
import * as React from "react";

export type QESButtonTypes = "primary" |
  "primary-danger" |
  "secondary" |
  "secondary-danger" |
  "disabled" |
  "none" |
  "link";

// opting for a map here instead of an object purely for the typed keys
// for whatever reason TS can't do { [key: QESButtonTypes]: string }
export const QESButtonTypeClasses = new Map<QESButtonTypes, string>([
  ["primary", "qes-button-primary"],
  ["primary-danger", "qes-button-primary danger"],
  ["secondary", "qes-button-secondary"],
  ["secondary-danger", "qes-button-secondary danger"],
  ["disabled", "qes-button-primary disabled"],
  ["none", ""],
  ["link", "qes-button-secondary link-text-default"],
]);


export interface ButtonProps {
  /**
   * Metadata used for test automation. Each one needs to be unique, though consistent across builds, and should describe the item and location. SCSS BEM naming conventions are good for this.
   */
  qesTestId: string;
  /**
   * Assigns a ref to the button, so you can reference it from the parent component.
   */
  qesRef?: React.Ref<HTMLButtonElement>;
  /**
   *  Defines the appearance of the button.
   */
  qesType?: QESButtonTypes;
}

type FullButtonProps = ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * Primary component for UI interaction. We have two general types of buttons: <br>
 *  - Primary buttons should be used for the expected or intended action, i.e. "continue" <br>
 *  - Secondary buttons should be used for other actions, i.e. "cancel" <br>
 * And then, we have a few different color options with intended meanings: <br>
 *  - Blue is the default <br>
 *  - Red ("danger") should be used when continuing will delete some data, e.g. overwriting terminations, removing projections <br>
 *  - Green ("success") can be used together with "danger" when there is a clear yes/no question (green for yes, red for no) <br>
 *  <br>
 * A button should be given the disabled attribute only when it is temporarily unavailable. If a user simply doesn't have access to an action, the button should not be rendered.<br>
 * Lastly, the component is written so that it will accept any attribute a native HTML button supports.
 * Note: If a button only contains an icon, an aria-label attribute should be included to describe the action (e.g. Delete for a trash can)
 * Note: Buttons that act as toggles without changing their label (e.g. to open a dropdown) should use the aria-pressed attribute (https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-pressed)
 */
export const QESButton: React.FC<React.PropsWithChildren<FullButtonProps>> = ({ qesRef, qesTestId, qesType, ...props }) => {
  return (
    <button
      {...props}
      type="button"
      className={cx({
        [QESButtonTypeClasses.get("none")]: qesType === "none",
        "qes-button": true,
        [QESButtonTypeClasses.get("primary")]: !qesType || qesType === "primary", // default
        [QESButtonTypeClasses.get("primary-danger")]: qesType === "primary-danger",
        [QESButtonTypeClasses.get("disabled")]: qesType === "disabled",
        [QESButtonTypeClasses.get("secondary")]: qesType === "secondary",
        [QESButtonTypeClasses.get("secondary-danger")]: qesType === "secondary-danger",
        [QESButtonTypeClasses.get("link")]: qesType === "link",
        [props.className]: !!props.className,

      })}
      data-testid={qesTestId}
      ref={qesRef as React.RefObject<HTMLButtonElement>}
    >
      {props.children}
    </button>
  );
};
